<template>
  <div class="company">
    <div v-if="!selectedRole" class="no company-card">
      <span>Please select a role</span>
    </div>
    <div v-if="!!selectedRole" class="company-info-container">
      <object-main-info
        :key="updateObjectMainInfoKey"
        :fields="fields"
        :identifier="role ? role.name : ''"
        :identifier-label="$tc('Label', 1)"
        :editing-data="editingData"
        :show-edit-button="
          actionToExecute !== 'addClientRoles' &&
          userHasPermission('role_update')
        "
        :card-label="cardLabel"
        :height="height"
        :submit-button-label="submitButtonLabel"
        :required-fields-filled="requiredFieldsAreFilled"
        @toggle-edit="toggleEdit"
        @update-object="updateCompany"
      >
        <template v-slot:buttons-before-edit-slot v-if="!showPermissions">
          <custom-button
            :disabled="permissionsButtonIsDisabled"
            :icon="'mdi-key'"
            :icon-size="13"
            :height="'20px'"
            :width="'20px'"
            :button-styling="buttonStyling"
            @buttonClick="togglePermissions"
          />
        </template>
        <template v-slot:before-fields-slot v-if="showPermissions">
          <div class="permissionMatrixDiv">
            <permission-matrix
              :data="role ? role.permissions : []"
              :disabled="!editingData"
              @change="checkIfRequiredFieldsAreFilled"
            />
          </div>
        </template>
        <template v-slot:before-title-slot v-if="showPermissions">
          <custom-button
            :icon="'mdi-chevron-left'"
            :icon-size="14"
            :height="'16px'"
            :width="'16px'"
            :button-styling="{ ...buttonStyling, 'box-shadow': 'none' }"
            :button-color="'transparent'"
            :icon-color="'var(--app-primary-base)'"
            @buttonClick="togglePermissions"
          />
        </template>
      </object-main-info>
      <role-specific-info
        v-if="!showPermissions"
        :specialDisable="actionToExecute === 'addClientRoles' ? false : true"
        :editingData="editingData"
        :role="role ? role : []"
        @cancel-edit="discardChangesDialogIsOpen = true"
        @save-material="saveChangesDialogIsOpen = true"
        :required-fields-filled="requiredFieldsAreFilled"
        :users="role ? role.users : []"
        :roles="role ? role.client_roles : []"
        :workspaces="role ? role.workspaces : []"
      />
    </div>

    <loading-spinner
      :active.sync="loading"
      :is-full-page="false"
      color="var(--app-primary-base)"
      style="position: absolute"
    />
    <dialog-choice
      :isOpen="saveChangesDialogIsOpen"
      :title="$t('SaveChanges')"
      :content="$t('SaveChangesContent')"
      :mainActionLabel="$t('Confirm')"
      @mainAction="saveObject"
      :secondaryActionLabel="$t('Cancel')"
      @secondaryAction="saveChangesDialogIsOpen = false"
      icon="mdi-content-save"
      iconColor="var(--app-primary-base)"
    />
    <dialog-choice
      :isOpen="discardChangesDialogIsOpen"
      :title="$t('DiscardChanges')"
      :content="$t('ClusterCreationConfirmation')"
      :mainActionLabel="$t('Confirm')"
      @mainAction="cancelEdit"
      :secondaryActionLabel="$t('Cancel')"
      @secondaryAction="discardChangesDialogIsOpen = false"
      icon="mdi-delete"
      iconColor="var(--app-primary-base)"
    />
  </div>
</template>

<script>
import { mapActions } from "vuex";
import { bus } from "@/main.js";
import _ from "lodash";
import utils from "@/utils";
import { checkPermissionsMixin } from "@/mixins/checkPermissionsMixin";
import { objectManagementMixin } from "@/mixins/objectManagementMixin";
import LoadingSpinner from "vue-loading-overlay";
import ObjectMainInfo from "@/newComponents/_Organisms/ObjectMainInfo";
import RoleSpecificInfo from "@/newComponents/SystemManagement/Roles/RoleSpecificInfo";
import DialogChoice from "../../_Molecules/DialogChoice.vue";
import CustomButton from "@/newComponents/_Atoms/CustomButton";
import PermissionMatrix from "@/newComponents/_Atoms/PermissionMatrix";
import { rolesListsMixin } from "@/mixins/management/rolesListsMixin";

export default {
  name: "Roles",

  components: {
    LoadingSpinner,
    DialogChoice,
    ObjectMainInfo,
    CustomButton,
    RoleSpecificInfo,
    PermissionMatrix,
  },

  mixins: [checkPermissionsMixin, objectManagementMixin, rolesListsMixin],

  props: {
    selectedRole: {
      type: Object,
    },
  },

  data() {
    return {
      loading: false,
      updateObjectMainInfoKey: 1,
      role: null,
      editingData: false,
      permissionDisable: false,
      roleLevelOptions: [],
      rolesToCopyFrom: [],
      originalCompany: {},
      copyFromDisable: true,
      specificRequiredFieldsAreFilled: false,
      requiredFieldsAreFilled: false,
      showPermissions: false,
      dataToSend: {},
      actionToExecute: "editClientRoleUuid",
      submitButtonLabel: "Save",
      cardLabel: "EditRole",
      saveChangesDialogIsOpen: false,
      discardChangesDialogIsOpen: false,
      buttonStyling: {
        padding: "0px",
        "drop-shadow": "0.85px 0.85px 1.75px rgba(0, 0, 0, 0.25)",
        "border-radius": "4px",
      },

      topCardHeight: {
        style: {
          maxHeight: "50%",
          height: "auto",
        },
      },
    };
  },

  mounted() {
    this.getSelectLists();
    bus.$on("update-company-bus", (company) => {
      // this.user.company = company
      this.checkIfRequiredFieldsAreFilled();
      this.updateCompanyOnCreate(company);
    });
    bus.$on("copyFromInputBus", (event) => {
      this.copyFromFunction(event);
    });
    bus.$on("copyFromEnable", (disable) => {
      this.copyFromDisable = disable;
    });
  },

  destroyed() {
    bus.$off("update-company-bus");
    bus.$off("copyFromInputBus");
    bus.$off("copyFromEnable");
  },

  watch: {
    selectedRole(value) {
      if (value !== null && Object.keys(value).length === 0) {
        this.setCreateCompanyVariables();
      } else if (!!value) {
        this.setEditCompanyVariables();
      } else if (value === null) {
        this.clearVariables();
      }
    },

    editingData() {
      bus.$emit("toggle-create-edit-flag")
    },
  },

  computed: {
    copyFromPlaceholder() {
      if (
        !this.role ||
        (this.role.company && Object.keys(this.role.company).length === 0) ||
        this.role.company === undefined
      ) {
        return this.$i18n.t("SelectCompanyBeforeCopyFrom");
      } else {
        return this.$i18n.t("Select");
      }
    },

    roleLevelDisable() {
      return (
        this.role.role_level === "super-admin" ||
        this.role.role_level === "admin"
      );
    },

    height() {
      if (this.editingData && this.showPermissions === false) {
        return "37%";
      }
      if (this.editingData && this.showPermissions) {
        return "100%";
      }
      if (this.editingData === false && this.showPermissions === false) {
        return "15%";
      }
      if (this.editingData === false && this.showPermissions) {
        return "100%";
      }
    },

    creatingCompany() {
      return this.actionToExecute === "addClientRoles";
    },

    fields() {
      let fields = [];
      if (this.role !== null && this.showPermissions === false) {
        fields = [
          {
            icon: "mdi-account-group",
            label: this.$i18n.t("Role Level"),
            value: this.role.role_level,
            varName: "role_level",
            inputType: "select",
            options: this.roleLevelOptions,
            disabled: this.roleLevelDisable,
            required: true,
            size: "100%",
          },
          {
            icon: "subject",
            label: this.$i18n.t("Description"),
            value: this.role.description,
            varName: "description",
            inputType: "textarea",
            disabled: false,
            required: true,
            size: "100%",
          },
        ];
      }

      if (this.editingData && this.showPermissions === false) {
        fields.splice(
          0,
          0,
          {
            icon: "subject",
            label: this.$i18n.t("Copy From"),
            value: this.role.copy_from,
            placeholder: this.copyFromPlaceholder,
            varName: "copy_from",
            inputType: "select",
            options: this.rolesToCopyFrom,
            disabled:
              this.role.role_level === "admin" ? true : this.copyFromDisable,
            required: false,
            size: "100%",
          },
          {
            icon: "subject",
            label: this.$i18n.tc("Label", 1),
            value: this.role.name,
            varName: "name",
            inputType: "text",
            disabled: false,
            required: true,
            size: "100%",
          }
        );
      }
      return fields;
    },

    /**
     * Type of companies
     * @returns {any}
     */
    companyTypes() {
      let normalizedCompanyTypes = [
        { code: "1", name: this.$i18n.t("ParentCompany") },
      ];
      this.$store.getters.company_types.forEach((type) => {
        normalizedCompanyTypes.push({
          code: type.id,
          name: type.label,
        });
      });
      return normalizedCompanyTypes;
    },

    defaultAvatarCompany() {
      return require("@/assets/management/avatar_company.png");
    },

    permissionsButtonIsDisabled() {
      return (
        !this.role.company ||
        Object.keys(this.role.company).length === 0 ||
        this.role.role_level === "super-admin" ||
        this.role.role_level === "admin"
      );
    },
  },

  methods: {
    ...mapActions([
      "fetchCompanyByID",
      "fetchAllClientRolesParamsId",
      "GET_LIST",
      "fetchPermissionsMatrix",
      "fetchAllClientRolesParams",
    ]),

    rolesFiltering(value) {
      let filteredRoles = value.filter(
        (role) =>
          role.role_level !== "admin" && role.role_level !== "super-admin"
      );
      return filteredRoles;
    },

    copyFromFunction(event) {
      const roleToCopy = this.rolesToCopyFrom.find((role) => role.id === event);
      this.role.description = roleToCopy.description;
      this.role.name = roleToCopy.name;
      this.role.role_level = roleToCopy.role_level;
      this.role.permissions = roleToCopy.permissions;
      this.role.copy_from = undefined;
      this.checkIfRequiredFieldsAreFilled();
      this.updateObjectMainInfoKey++;
    },

    updateCompanyOnCreate(company) {
      if (this.actionToExecute === "addClientRoles") {
        this.getPermissionMatrix(company.id);
        if (company) {
          this.rolesToCopyFrom = company.roles
            .filter(
              (role) =>
                role.role_level !== "admin" && role.role_level !== "super-admin"
            )
            .map((role) => ({
              ...role,
              code: role.id,
            }));
        }
        // this.permissionDisable = false
        // this.copyFromDisable = false
        this.role.copy_from = undefined;
        // this.company.description = undefined
        // this.company.name = undefined
        // this.company.role_level = undefined
        // this.company.permissions = []
        this.updateObjectMainInfoKey++;
      }
      this.checkIfRequiredFieldsAreFilled();
    },

    async getAllRolesToCopy() {
      this.loading = true;
      try {
        const result = await this.fetchAllClientRolesParams({
          paginate: false,
        });
        if (result.data) {
          this.rolesToCopyFrom = result.data.map((role) => ({
            ...role,
            code: role.id,
          }));
          this.rolesToCopyFrom = this.rolesFiltering(this.rolesToCopyFrom);
        }
      } catch (error) {
        console.error(error);
      } finally {
        this.loading = false;
      }
    },

    async getPermissionMatrix(companyId) {
      this.loading = true;
      try {
        const result = await this.fetchPermissionsMatrix(companyId);
        this.role.permissions = result;
      } catch (error) {
        console.error(error);
      } finally {
        this.loading = false;
      }
    },

    async getSelectLists() {
      this.loading = true;
      try {
        const result = (this.roleLevelOptions = await this.GET_LIST({
          listName: "list_role_level",
        }));
        const filteredRoles = result.map((role) => {
          if (role.code === "super-admin" || role.code === "admin") {
            return { ...role, disabled: true };
          }
          return role;
        });
        this.roleLevelOptions = filteredRoles;
      } catch (error) {
        console.error(error);
      } finally {
        this.loading = false;
      }
    },

    togglePermissions() {
      this.showPermissions = !this.showPermissions;
    },

    async getCompanyByID() {
      let id = this.selectedRole.id;
      // let includes = this.getIncludes();

      this.loading = true;
      try {
        const result = await this.fetchAllClientRolesParamsId(id);

        this.role = result.data[0];

        this.originalCompany = _.cloneDeep(this.role);

        //this.separateUsersByType();
      } catch (error) {
        await this.$router.replace("/error");
        console.error(error);
      } finally {
        this.loading = false;
      }
    },

    /**
     * Returns what should be included in the go fetch a company,
     * according to the user's permissions
     * @returns {string}
     */
    // getIncludes() {
    //   let includes = "";
    //   if (this.userHasManagementPermission("user_company_list")) {
    //     includes += "users,";
    //   }
    //   if (this.userHasManagementPermission("company_permission_list")) {
    //     includes += "permissions,";
    //     includes += "roles,";
    //   }
    //   if (this.userHasManagementPermission("workspace_company_list")) {
    //     includes += "workspaces,";
    //   }
    //   if (this.userHasManagementPermission("client_role_list")) {
    //     includes += "client_roles";
    //   }
    //   return includes;
    // },

    /**
     * Separates users by type (mobile|web)
     */
    separateUsersByType() {
      this.role.webAssociates = [];
      this.role.mobileAssociates = [];
      for (let user of this.originalCompany.users) {
        if (user.type === "web") {
          this.role.webAssociates.push(user);
        } else {
          this.role.mobileAssociates.push(user);
        }
      }
    },

    updateCompany(updatedValue) {
      this.role[updatedValue.varName] = updatedValue.value;

      this.checkIfRequiredFieldsAreFilled();
    },

    checkIfRequiredFieldsAreFilled() {
      this.requiredFieldsAreFilled =
        this.role &&
        this.role.company &&
        Object.keys(this.role.company).length > 0
          ? !!this.role.description &&
            !!this.role.name &&
            !!this.role.role_level
          : false;
    },

    cancelEdit() {
      this.discardChangesDialogIsOpen = false;
      this.editingData = false;
      if (this.role.id) {
        this.role = _.cloneDeep(this.originalCompany);
      } else {
        this.$emit("clear-selected-item");
      }
      this.checkIfRequiredFieldsAreFilled();
    },

    toggleEdit() {
      if (this.editingData) {
        this.discardChangesDialogIsOpen = true;
      } else {
        this.editingData = !this.editingData;
      }
      this.checkIfRequiredFieldsAreFilled();
    },

    async saveObject() {
      this.saveChangesDialogIsOpen = false;
      try {
        this.loading = true;

        this.createDataToSend();

        if (Object.keys(this.dataToSend).length > 0) {
          if (
            !this.creatingCompany &&
            this.actionToExecute === "editClientRoleUuid"
          ) {
            this.dataToSend.uuid = this.role.id;
          }
          await this.$store.dispatch(this.actionToExecute, this.dataToSend);
        }

        this.editingData = false;

        if (!this.creatingCompany) {
          this.getCompanyByID();
          this.$toasted.global.toast_success(
            this.$i18n.t("RoleUpdatedSuccessfully")
          );
        } else {
          this.$toasted.global.toast_success(
            this.$i18n.t("RoleCreatedSuccessfully")
          );
        }

        bus.$emit("reload-roles");
      } catch (error) {
        console.error(error);
        this.handleError(error);
      } finally {
        this.loading = false;
        this.dataToSend = {};
        this.checkIfRequiredFieldsAreFilled();
      }
    },

    /** Fills the object to send to the server by putting a watcher in each variable of the object */
    createDataToSend() {
      Object.keys(this.role).forEach((key) => {
        if (this.role[key] !== this.originalCompany[key]) {
          this.dataToSend[key] = this.role[key];
        }
      });
      if (this.actionToExecute === "editClientRoleUuid") {
        this.dataToSend.company_id = this.role.company.id;
        delete this.dataToSend.role;
        delete this.dataToSend.users;
        this.dataToSend = { updatedData: this.dataToSend };
      }
      if (this.actionToExecute === "addClientRoles") {
        delete this.dataToSend.role;
        this.dataToSend.company_id = this.role.company.id;
        this.dataToSend.user_type = "web";
        this.dataToSend = this.dataToSend;
      }
    },

    handleError(error) {
      let errorHandle = utils.errorHandler(error);
      if (typeof errorHandle === "object") {
        if (errorHandle.hasOwnProperty("email")) {
          this.$toasted.global.toast_error(this.$i18n.t("ValidEmail"));
        }
        if (errorHandle.hasOwnProperty("address")) {
          this.$toasted.global.toast_error(this.$i18n.t("ValidAdress"));
        }
        if (errorHandle.hasOwnProperty("name")) {
          this.$toasted.global.toast_error(this.$i18n.t("ValidName"));
        }
        if (errorHandle.hasOwnProperty("image")) {
          this.$toasted.global.toast_error(this.$i18n.t("ValidImage"));
        }
        if (errorHandle.hasOwnProperty("company_id")) {
          this.$toasted.global.toast_error(this.$i18n.t("ValidParentCompany"));
        }
      } else if (typeof errorHandle === "string") {
        this.$toasted.global.toast_error(errorHandle);
      }
    },

    setCreateCompanyVariables() {
      this.showPermissions = false;
      this.permissionDisable = true;
      this.copyFromDisable = true;
      this.role = {
        description: "",
        name: "",
        company: {},
      };
      this.originalCompany = {};
      this.actionToExecute = "addClientRoles";
      this.submitButtonLabel = "Create";
      this.cardLabel = "New Role";
      this.editingData = true;
      this.checkIfRequiredFieldsAreFilled();
    },

    setEditCompanyVariables() {
      this.getAllRolesToCopy();
      this.getCompanyByID();
      this.copyFromDisable = false;
      this.actionToExecute = "editClientRoleUuid";
      this.submitButtonLabel = "Save";
      this.cardLabel = "Edit Role";
      this.checkIfRequiredFieldsAreFilled();
    },

    clearVariables() {
      this.role = null;
      this.originalCompany = null;
      this.editingData = false;
      this.permissionDisable = false;
      this.copyFromDisable = false;
      this.showPermissions = false;
      this.checkIfRequiredFieldsAreFilled();
    },

    requiredFieldsValidation() {
      return this.requiredFieldsAreFilled;
    },

    deleteCompanyImage() {
      this.updateCompany({
        varName: "image",
        value: this.defaultAvatarCompany,
      });
    },

    /**
     * Initiates the process of submitting an image file.
     */
    submitFile() {
      this.$refs.file.click();
    },

    /**
     * Initiates the process of preview aof the submitted image file.
     */
    previewFile() {
      this.$emit("imageValidation", false);
      let file = this.$refs.file.files[0];
      if (!file) {
        return;
      }
      this.createPreview(file);
    },

    /**
     * Obtains a readable image from the file submitted.
     * @param file
     */
    createPreview(file) {
      let reader = new FileReader();

      reader.onload = (e) => {
        this.updateCompany("image", e.target.result);
      };
      reader.readAsDataURL(file);
    },
  },
};
</script>

<style scoped>
.permissionMatrixDiv {
  margin-top: 16px;
  padding-bottom: 16px;
}

.company {
  height: 100%;
  max-height: 100%;
  background-color: #fff;
  position: relative;
  border-radius: 8px;
}

.company-card {
  height: 100%;
}

.company-card {
  box-shadow: 0px 2px 2px 0px rgba(0, 0, 0, 0.25);
  border-radius: 8px;
}

.no.company-card {
  display: flex;
  flex-direction: column;
  align-content: center;
  justify-content: center;
}

.no.company-card > span {
  color: var(--app-primary-base);
  text-align: center;
  font-size: 12px;
  font-weight: 600;
  line-height: normal;
}

.company-info-container {
  gap: 8px;
  display: flex;
  flex-direction: column;
  max-height: 100%;
  height: 100%;
}

.company-logo {
  background-color: white;
  border-radius: 8px;
  height: 40px;
  width: 40px;
  box-shadow: 1.75px 1.75px 4.75px 0px rgba(0, 0, 0, 0.25);
  margin-right: 16px;
  margin-left: 2px;
}

.edit-image-container {
  display: flex;
  flex-direction: row;
  align-items: center;
  margin: 10px 0;
}
</style>



// WEBPACK FOOTER //
// src/newComponents/SystemManagement/Roles/Roles.vue